import React, { useState, useEffect } from 'react';
import { X, Calendar, Clock, User, Mail, Phone, MapPin, CreditCard } from 'lucide-react';
import { Button } from './ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const BookingModal = ({ isOpen, onClose, serviceName }) => {
  const [step, setStep] = useState(1); // 1: Patient Info, 2: Select Time, 3: Confirm
  const [loading, setLoading] = useState(false);
  const [practitioners, setPractitioners] = useState([]);
  const [availableSlots, setAvailableSlots] = useState([]);
  
  // Form data
  const [patientData, setPatientData] = useState({
    title: 'Mr',
    first_name: '',
    last_name: '',
    date_of_birth: '',
    email: '',
    phone_number: '',
    address_line_1: '',
    city: '',
    postal_code: '',
  });
  
  const [bookingData, setBookingData] = useState({
    selectedDate: '',
    selectedPractitioner: '',
    selectedSlot: null,
    reason: serviceName || 'Exam',
  });
  
  const [confirmation, setConfirmation] = useState(null);

  useEffect(() => {
    if (isOpen && step === 2) {
      fetchPractitioners();
    }
  }, [isOpen, step]);

  useEffect(() => {
    if (bookingData.selectedDate && bookingData.selectedPractitioner) {
      fetchAvailableSlots();
    }
  }, [bookingData.selectedDate, bookingData.selectedPractitioner]);

  const fetchPractitioners = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/dentally/practitioners`);
      const data = await response.json();
      if (data.success) {
        setPractitioners(data.practitioners);
        // Auto-select first practitioner if available
        if (data.practitioners.length > 0) {
          setBookingData(prev => ({ ...prev, selectedPractitioner: data.practitioners[0].id }));
        }
      }
    } catch (error) {
      console.error('Error fetching practitioners:', error);
    }
  };

  const fetchAvailableSlots = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/dentally/availability?start_date=${bookingData.selectedDate}&practitioner_ids=${bookingData.selectedPractitioner}&duration_minutes=30`
      );
      const data = await response.json();
      if (data.success) {
        setAvailableSlots(data.slots);
      }
    } catch (error) {
      console.error('Error fetching availability:', error);
      setAvailableSlots([]);
    } finally {
      setLoading(false);
    }
  };

  const handlePatientSubmit = async (e) => {
    e.preventDefault();
    setStep(2);
  };

  const handleBookingSubmit = async () => {
    if (!bookingData.selectedSlot) {
      alert('Please select a time slot');
      return;
    }

    setLoading(true);
    try {
      // Step 1: Create patient
      const patientResponse = await fetch(`${BACKEND_URL}/api/dentally/patients/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(patientData),
      });
      
      const patientResult = await patientResponse.json();
      
      if (!patientResult.success) {
        throw new Error(patientResult.detail || 'Failed to create patient');
      }

      // Step 2: Book appointment
      const appointmentResponse = await fetch(`${BACKEND_URL}/api/dentally/appointments/book`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          patient_id: patientResult.patient_id,
          practitioner_id: parseInt(bookingData.selectedPractitioner),
          start_time: bookingData.selectedSlot.start_time,
          duration_minutes: 30,
          reason: bookingData.reason,
          notes: `Booked online for ${serviceName || 'service'}`,
        }),
      });

      const appointmentResult = await appointmentResponse.json();

      if (appointmentResult.success) {
        setConfirmation({
          appointmentId: appointmentResult.appointment_id,
          patientName: `${patientData.first_name} ${patientData.last_name}`,
          date: new Date(bookingData.selectedSlot.start_time).toLocaleDateString(),
          time: new Date(bookingData.selectedSlot.start_time).toLocaleTimeString(),
        });
        setStep(3);
      } else {
        throw new Error(appointmentResult.detail || 'Failed to book appointment');
      }
    } catch (error) {
      console.error('Booking error:', error);
      alert(`Booking failed: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setStep(1);
    setPatientData({
      title: 'Mr',
      first_name: '',
      last_name: '',
      date_of_birth: '',
      email: '',
      phone_number: '',
      address_line_1: '',
      city: '',
      postal_code: '',
    });
    setBookingData({
      selectedDate: '',
      selectedPractitioner: '',
      selectedSlot: null,
      reason: serviceName || 'Exam',
    });
    setConfirmation(null);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center">
          <h2 className="text-2xl font-semibold text-rabbit">
            {step === 3 ? 'Booking Confirmed!' : 'Book Your Appointment'}
          </h2>
          <button onClick={handleClose} className="text-chocolate-malt hover:text-burnt-coral">
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Progress Steps */}
        {step < 3 && (
          <div className="px-6 py-4 border-b">
            <div className="flex items-center justify-between">
              <div className={`flex items-center ${step >= 1 ? 'text-burnt-coral' : 'text-gray-400'}`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step >= 1 ? 'bg-burnt-coral text-white' : 'bg-gray-200'}`}>
                  1
                </div>
                <span className="ml-2 text-sm font-medium">Your Details</span>
              </div>
              <div className="flex-1 h-0.5 mx-4 bg-gray-200">
                <div className={`h-full ${step >= 2 ? 'bg-burnt-coral' : 'bg-gray-200'}`} style={{ width: step >= 2 ? '100%' : '0%' }}></div>
              </div>
              <div className={`flex items-center ${step >= 2 ? 'text-burnt-coral' : 'text-gray-400'}`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step >= 2 ? 'bg-burnt-coral text-white' : 'bg-gray-200'}`}>
                  2
                </div>
                <span className="ml-2 text-sm font-medium">Select Time</span>
              </div>
            </div>
          </div>
        )}

        {/* Content */}
        <div className="p-6">
          {/* Step 1: Patient Information */}
          {step === 1 && (
            <form onSubmit={handlePatientSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-chocolate-malt mb-1">
                    Title *
                  </label>
                  <select
                    required
                    value={patientData.title}
                    onChange={(e) => setPatientData({ ...patientData, title: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-burnt-coral"
                  >
                    <option value="Mr">Mr</option>
                    <option value="Mrs">Mrs</option>
                    <option value="Miss">Miss</option>
                    <option value="Ms">Ms</option>
                    <option value="Dr">Dr</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-chocolate-malt mb-1">
                    First Name *
                  </label>
                  <input
                    type="text"
                    required
                    value={patientData.first_name}
                    onChange={(e) => setPatientData({ ...patientData, first_name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-burnt-coral"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-chocolate-malt mb-1">
                    Last Name *
                  </label>
                  <input
                    type="text"
                    required
                    value={patientData.last_name}
                    onChange={(e) => setPatientData({ ...patientData, last_name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-burnt-coral"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-chocolate-malt mb-1">
                    Date of Birth *
                  </label>
                  <input
                    type="date"
                    required
                    value={patientData.date_of_birth}
                    onChange={(e) => setPatientData({ ...patientData, date_of_birth: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-burnt-coral"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-chocolate-malt mb-1">
                    Email
                  </label>
                  <input
                    type="email"
                    value={patientData.email}
                    onChange={(e) => setPatientData({ ...patientData, email: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-burnt-coral"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-chocolate-malt mb-1">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    value={patientData.phone_number}
                    onChange={(e) => setPatientData({ ...patientData, phone_number: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-burnt-coral"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-chocolate-malt mb-1">
                    Address *
                  </label>
                  <input
                    type="text"
                    required
                    value={patientData.address_line_1}
                    onChange={(e) => setPatientData({ ...patientData, address_line_1: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-burnt-coral"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-chocolate-malt mb-1">
                    City *
                  </label>
                  <input
                    type="text"
                    required
                    value={patientData.city}
                    onChange={(e) => setPatientData({ ...patientData, city: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-burnt-coral"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-chocolate-malt mb-1">
                    Postcode *
                  </label>
                  <input
                    type="text"
                    required
                    value={patientData.postal_code}
                    onChange={(e) => setPatientData({ ...patientData, postal_code: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-burnt-coral"
                  />
                </div>
              </div>

              <Button type="submit" className="w-full bg-burnt-coral hover:bg-burnt-coral/90">
                Continue to Select Time
              </Button>
            </form>
          )}

          {/* Step 2: Select Time */}
          {step === 2 && (
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-chocolate-malt mb-2">
                  Select Date
                </label>
                <input
                  type="date"
                  value={bookingData.selectedDate}
                  onChange={(e) => setBookingData({ ...bookingData, selectedDate: e.target.value, selectedSlot: null })}
                  min={new Date().toISOString().split('T')[0]}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-burnt-coral"
                />
              </div>

              {bookingData.selectedDate && (
                <div>
                  <label className="block text-sm font-medium text-chocolate-malt mb-2">
                    Available Times
                  </label>
                  {loading ? (
                    <p className="text-center py-8 text-chocolate-malt">Loading available times...</p>
                  ) : availableSlots.length === 0 ? (
                    <p className="text-center py-8 text-chocolate-malt">No available slots for this date. Please try another date.</p>
                  ) : (
                    <div className="grid grid-cols-3 md:grid-cols-4 gap-2">
                      {availableSlots.map((slot, index) => {
                        const startTime = new Date(slot.start_time);
                        const timeString = startTime.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
                        const isSelected = bookingData.selectedSlot?.start_time === slot.start_time;
                        
                        return (
                          <button
                            key={index}
                            onClick={() => setBookingData({ ...bookingData, selectedSlot: slot })}
                            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                              isSelected
                                ? 'bg-burnt-coral text-white'
                                : 'bg-gray-100 text-chocolate-malt hover:bg-burnt-coral/10'
                            }`}
                          >
                            {timeString}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              <div className="flex gap-3">
                <Button
                  onClick={() => setStep(1)}
                  variant="outline"
                  className="flex-1"
                >
                  Back
                </Button>
                <Button
                  onClick={handleBookingSubmit}
                  disabled={!bookingData.selectedSlot || loading}
                  className="flex-1 bg-burnt-coral hover:bg-burnt-coral/90"
                >
                  {loading ? 'Booking...' : 'Confirm Booking'}
                </Button>
              </div>
            </div>
          )}

          {/* Step 3: Confirmation */}
          {step === 3 && confirmation && (
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              
              <h3 className="text-2xl font-semibold text-rabbit mb-2">Booking Confirmed!</h3>
              <p className="text-chocolate-malt mb-6">
                Your appointment has been successfully booked.
              </p>

              <div className="bg-gray-50 rounded-lg p-6 text-left space-y-3">
                <div className="flex items-center gap-3">
                  <User className="w-5 h-5 text-burnt-coral" />
                  <div>
                    <p className="text-sm text-chocolate-malt">Patient</p>
                    <p className="font-medium">{confirmation.patientName}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Calendar className="w-5 h-5 text-burnt-coral" />
                  <div>
                    <p className="text-sm text-chocolate-malt">Date</p>
                    <p className="font-medium">{confirmation.date}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Clock className="w-5 h-5 text-burnt-coral" />
                  <div>
                    <p className="text-sm text-chocolate-malt">Time</p>
                    <p className="font-medium">{confirmation.time}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <MapPin className="w-5 h-5 text-burnt-coral" />
                  <div>
                    <p className="text-sm text-chocolate-malt">Location</p>
                    <p className="font-medium">64a Horn Lane, Acton, London W3 6NP</p>
                  </div>
                </div>
              </div>

              <p className="text-sm text-chocolate-malt mt-6">
                A confirmation email will be sent shortly.
              </p>

              <Button
                onClick={handleClose}
                className="mt-6 bg-burnt-coral hover:bg-burnt-coral/90"
              >
                Close
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BookingModal;
